/**
 * BikerAI — pure biker (mounted) behaviour state machine.
 * No Phaser imports — fully testable in Node.js.
 *
 * Behaviour: drive-by strafes across the player's lane.
 * Taking enough hits while mounted knocks the rider off.
 * After wantsKnockOff fires, the Phaser adapter should:
 *   • spawn a grounded "downed-rider" enemy at the biker's position
 *   • leave the bike body as a floor hazard (MY-1800)
 *
 * Phases:
 *   biker_approach  — adjust Y to align with target's lane while entering at approach speed
 *   biker_strafe    — cross-screen at STRAFE_SPEED; contact damage when Y-aligned
 *   biker_recover   — off-screen cooldown before the next pass
 *   biker_downed    — rider knocked off; no further movement
 */

export type BikerPhase = 'biker_approach' | 'biker_strafe' | 'biker_recover' | 'biker_downed';

/** Speed multiplier during the active strafe pass (scaled by entity.speed in the adapter). */
export const STRAFE_SPEED = 5.0;
/** Speed multiplier while approaching the player's Y lane. */
export const STRAFE_APPROACH_SPEED = 2.5;
/** Y-distance (px) at which the biker is considered lane-aligned → enters strafe. */
export const BIKER_LANE_TOLERANCE = 20;
/** Y-distance (px) at which the strafe triggers contact damage. */
export const BIKER_CONTACT_RANGE = 60;
/** Default hits taken while mounted before the rider is knocked off. */
export const KNOCK_OFF_HITS = 3;
/** Frames of off-screen recovery between strafe passes. */
export const BIKER_RECOVER_FRAMES = 90;
/** Screen-edge margin (px) that ends the strafe and starts recovery. */
export const BIKER_EDGE_MARGIN = 40;

export interface BikerAIState {
  phase: BikerPhase;
  /** Horizontal travel direction: 1 = left→right, -1 = right→left. */
  strafeDir: 1 | -1;
  /** Hits absorbed while still mounted. */
  mountedHits: number;
  /** True once the knock-off transition has fired. */
  isKnockedOff: boolean;
  /** Frames remaining in recovery before next approach. */
  recoverFrames: number;
}

export interface BikerAIInput {
  selfX: number;
  selfY: number;
  /** Y position of the target (player). */
  targetY: number;
  /** True when a new hit registered on the mounted biker this frame. */
  hitRegisteredThisFrame: boolean;
  /** World width for strafe edge detection. */
  worldWidth: number;
  /**
   * Knock-off hit count override.
   * Defaults to KNOCK_OFF_HITS when omitted.
   */
  knockOffHits?: number;
}

export interface BikerAIOutput {
  moveX: number;
  moveY: number;
  /** True this frame when the biker is in contact range during a strafe. */
  wantsStrafeContact: boolean;
  /** True on the single frame the knock-off transition should trigger. */
  wantsKnockOff: boolean;
  /** True in all frames after the knock-off event (downed phase). */
  isKnockedOff: boolean;
  nextState: BikerAIState;
}

export function initialBikerState(startDir: 1 | -1 = 1): BikerAIState {
  return {
    phase: 'biker_approach',
    strafeDir: startDir,
    mountedHits: 0,
    isKnockedOff: false,
    recoverFrames: 0,
  };
}

/**
 * Advance the biker state machine by one frame.
 * Pure function — the Phaser adapter (Enemy.ts) applies movement and spawns hitboxes.
 */
export function tickBikerAI(input: BikerAIInput, state: BikerAIState): BikerAIOutput {
  if (state.isKnockedOff) {
    return downed(state);
  }

  const knockOffThreshold = input.knockOffHits ?? KNOCK_OFF_HITS;
  const mountedHits = state.mountedHits + (input.hitRegisteredThisFrame ? 1 : 0);

  if (mountedHits >= knockOffThreshold) {
    return {
      moveX: 0,
      moveY: 0,
      wantsStrafeContact: false,
      wantsKnockOff: true,
      isKnockedOff: true,
      nextState: {
        ...state,
        phase: 'biker_downed',
        mountedHits,
        isKnockedOff: true,
      },
    };
  }

  switch (state.phase) {
    case 'biker_approach':
      return tickApproach(input, state, mountedHits);
    case 'biker_strafe':
      return tickStrafe(input, state, mountedHits);
    case 'biker_recover':
      return tickRecover(state, mountedHits);
    default:
      return downed(state);
  }
}

// ─── Phase helpers ────────────────────────────────────────────────────────────

function tickApproach(
  input: BikerAIInput,
  state: BikerAIState,
  mountedHits: number,
): BikerAIOutput {
  const dy = input.targetY - input.selfY;
  const absDy = Math.abs(dy);

  if (absDy <= BIKER_LANE_TOLERANCE) {
    // Lane-aligned — enter the fast strafe pass
    return {
      moveX: state.strafeDir * STRAFE_APPROACH_SPEED,
      moveY: 0,
      wantsStrafeContact: false,
      wantsKnockOff: false,
      isKnockedOff: false,
      nextState: { ...state, phase: 'biker_strafe', mountedHits },
    };
  }

  return {
    moveX: state.strafeDir * STRAFE_APPROACH_SPEED,
    moveY: Math.sign(dy) * STRAFE_APPROACH_SPEED,
    wantsStrafeContact: false,
    wantsKnockOff: false,
    isKnockedOff: false,
    nextState: { ...state, phase: 'biker_approach', mountedHits },
  };
}

function tickStrafe(input: BikerAIInput, state: BikerAIState, mountedHits: number): BikerAIOutput {
  const atEdge =
    input.selfX <= BIKER_EDGE_MARGIN || input.selfX >= input.worldWidth - BIKER_EDGE_MARGIN;

  if (atEdge) {
    const nextDir = (state.strafeDir === 1 ? -1 : 1) as 1 | -1;
    return {
      moveX: state.strafeDir * STRAFE_SPEED,
      moveY: 0,
      wantsStrafeContact: false,
      wantsKnockOff: false,
      isKnockedOff: false,
      nextState: {
        ...state,
        phase: 'biker_recover',
        strafeDir: nextDir,
        mountedHits,
        recoverFrames: BIKER_RECOVER_FRAMES,
      },
    };
  }

  const inContactRange = Math.abs(input.selfY - input.targetY) <= BIKER_CONTACT_RANGE;
  return {
    moveX: state.strafeDir * STRAFE_SPEED,
    moveY: 0,
    wantsStrafeContact: inContactRange,
    wantsKnockOff: false,
    isKnockedOff: false,
    nextState: { ...state, phase: 'biker_strafe', mountedHits },
  };
}

function tickRecover(state: BikerAIState, mountedHits: number): BikerAIOutput {
  const recoverFrames = Math.max(0, state.recoverFrames - 1);

  if (recoverFrames === 0) {
    return {
      moveX: 0,
      moveY: 0,
      wantsStrafeContact: false,
      wantsKnockOff: false,
      isKnockedOff: false,
      nextState: { ...state, phase: 'biker_approach', mountedHits, recoverFrames: 0 },
    };
  }

  return {
    moveX: 0,
    moveY: 0,
    wantsStrafeContact: false,
    wantsKnockOff: false,
    isKnockedOff: false,
    nextState: { ...state, phase: 'biker_recover', mountedHits, recoverFrames },
  };
}

function downed(state: BikerAIState): BikerAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    wantsStrafeContact: false,
    wantsKnockOff: false,
    isKnockedOff: true,
    nextState: { ...state, phase: 'biker_downed', isKnockedOff: true },
  };
}
